import { useEffect, useRef, useState } from "react";
import axios from "axios";
import { motion, AnimatePresence } from "framer-motion";
import { MessageCircle, X, Send } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
const EASE = [0.16, 1, 0.3, 1];

export default function FloatingChat({ t, lang, open, onOpenChange }) {
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [message, setMessage] = useState("");
  const [status, setStatus] = useState("idle");
  const [error, setError] = useState("");
  const inputRef = useRef(null);

  useEffect(() => {
    if (open && inputRef.current) {
      const timer = setTimeout(() => inputRef.current?.focus(), 400);
      return () => clearTimeout(timer);
    }
  }, [open]);

  const reset = () => {
    setName("");
    setEmail("");
    setMessage("");
    setError("");
    setStatus("idle");
  };

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    if (!EMAIL_RE.test(email.trim())) {
      setError(t.chat.invalidEmail);
      return;
    }
    if (!message.trim()) {
      setError(t.chat.emptyMessage);
      return;
    }
    setStatus("sending");
    try {
      await axios.post(`${API}/contact`, {
        name: name.trim(),
        email: email.trim(),
        message: message.trim(),
        lang,
      });
      setStatus("done");
    } catch {
      setError(t.chat.error);
      setStatus("idle");
    }
  };

  return (
    <>
      {/* Floating trigger */}
      <button
        data-testid="chat-floating-button"
        type="button"
        onClick={() => onOpenChange(!open)}
        aria-label={open ? t.chat.closeLabel : t.chat.openLabel}
        className={`fixed bottom-6 right-6 z-[70] flex items-center gap-2 rounded-full bg-[#00F562] px-5 py-4 font-mono text-xs font-extrabold uppercase tracking-widest text-[#06080C] shadow-[0_10px_40px_rgba(0,245,98,0.35)] transition-all duration-300 hover:scale-105 hover:shadow-[0_15px_50px_rgba(0,245,98,0.5)] sm:bottom-8 sm:right-8 ${
          open ? "pointer-events-none opacity-0" : "opacity-100"
        }`}
      >
        <MessageCircle size={18} strokeWidth={2.5} />
        <span className="hidden sm:inline">{t.chat.floatingLabel}</span>
      </button>

      {/* Chat panel */}
      <AnimatePresence>
        {open && (
          <motion.div
            data-testid="chat-panel"
            initial={{ opacity: 0, y: 40, scale: 0.95 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            exit={{ opacity: 0, y: 40, scale: 0.95 }}
            transition={{ duration: 0.35, ease: EASE }}
            className="fixed bottom-6 right-6 z-[71] flex w-[calc(100%-3rem)] max-w-sm flex-col overflow-hidden rounded-2xl border border-white/10 bg-[#0D111A] shadow-[0_30px_80px_rgba(0,0,0,0.65)] sm:bottom-8 sm:right-8"
            style={{ maxHeight: "calc(100vh - 4rem)" }}
          >
            {/* Header */}
            <div className="relative flex items-start gap-3 border-b border-white/10 bg-gradient-to-br from-[#0D111A] via-[#0D111A] to-[#00F562]/[0.08] p-5">
              <div className="flex h-11 w-11 flex-shrink-0 items-center justify-center rounded-xl bg-[#00F562]">
                <span className="font-display text-base font-black leading-none tracking-tight text-[#06080C]">
                  TH
                </span>
              </div>
              <div className="flex-1 pt-0.5">
                <p className="font-display text-lg font-black uppercase tracking-wide text-white">
                  {t.chat.title}
                </p>
                <p className="mt-0.5 text-xs leading-snug text-slate-400">
                  {t.chat.subtitle}
                </p>
              </div>
              <button
                data-testid="chat-close-button"
                type="button"
                onClick={() => onOpenChange(false)}
                aria-label={t.chat.closeLabel}
                className="flex h-8 w-8 items-center justify-center rounded-full border border-white/10 text-slate-400 transition-colors duration-300 hover:border-[#00F562]/50 hover:text-[#00F562]"
              >
                <X size={14} />
              </button>
            </div>

            {/* Body */}
            <div className="max-h-[60vh] overflow-y-auto p-5">
              {status === "done" ? (
                <motion.div
                  key="success"
                  data-testid="chat-success"
                  initial={{ opacity: 0, y: 12 }}
                  animate={{ opacity: 1, y: 0 }}
                  className="py-8 text-center"
                >
                  <div className="mx-auto flex h-14 w-14 items-center justify-center rounded-full bg-[#00F562]/15 text-[#00F562]">
                    <Send size={20} />
                  </div>
                  <p className="mt-4 font-display text-base font-extrabold uppercase tracking-wide text-white">
                    {t.chat.successTitle}
                  </p>
                  <p className="mt-2 text-sm leading-relaxed text-slate-400">
                    {t.chat.successBody}
                  </p>
                  <button
                    data-testid="chat-new-message"
                    type="button"
                    onClick={reset}
                    className="mt-6 rounded-full border border-white/15 px-5 py-2.5 font-mono text-[10px] font-bold uppercase tracking-widest text-slate-200 transition-colors duration-300 hover:border-[#00F562]/60 hover:text-[#00F562]"
                  >
                    {t.chat.newMessage}
                  </button>
                </motion.div>
              ) : (
                <form onSubmit={submit} className="space-y-3">
                  <input
                    ref={inputRef}
                    data-testid="chat-name-input"
                    type="text"
                    value={name}
                    onChange={(e) => setName(e.target.value)}
                    placeholder={t.chat.namePlaceholder}
                    maxLength={120}
                    className="w-full rounded-xl border border-white/10 bg-[#06080C] px-4 py-3 text-sm text-white placeholder-slate-500 outline-none transition-colors duration-300 focus:border-[#00F562]/60"
                  />
                  <input
                    data-testid="chat-email-input"
                    type="email"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    placeholder={t.chat.emailPlaceholder}
                    required
                    className="w-full rounded-xl border border-white/10 bg-[#06080C] px-4 py-3 text-sm text-white placeholder-slate-500 outline-none transition-colors duration-300 focus:border-[#00F562]/60"
                  />
                  <textarea
                    data-testid="chat-message-input"
                    value={message}
                    onChange={(e) => setMessage(e.target.value)}
                    placeholder={t.chat.messagePlaceholder}
                    rows={4}
                    maxLength={4000}
                    required
                    className="w-full resize-none rounded-xl border border-white/10 bg-[#06080C] px-4 py-3 text-sm text-white placeholder-slate-500 outline-none transition-colors duration-300 focus:border-[#00F562]/60"
                  />
                  {error && (
                    <p
                      data-testid="chat-error"
                      className="text-xs text-red-400"
                    >
                      {error}
                    </p>
                  )}
                  <button
                    data-testid="chat-submit-button"
                    type="submit"
                    disabled={status === "sending"}
                    className="flex w-full items-center justify-center gap-2 rounded-full bg-[#00F562] px-5 py-3 font-mono text-xs font-extrabold uppercase tracking-widest text-[#06080C] transition-transform duration-300 hover:scale-[1.02] disabled:opacity-60"
                  >
                    <Send size={14} />
                    {status === "sending" ? t.chat.sending : t.chat.send}
                  </button>
                </form>
              )}
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
}
